import { useEffect, useState } from 'react';
import type {
  // ApplicationState, CreateApplicationRequest — shartnoma qaytarilganda
  IdentifiedClient,
  Provider,
  ScoringSession,
  StartIdentificationRequest,
  IdentificationStatus,
  StartIdentificationResponse,
  VerifyIdentificationResponse,
} from '@asko/shared';
import { isMyIdSuccess, ProviderResultStatus } from '@asko/shared';
import { Stepper, type StepId } from '../components/Stepper';
import { AnketaForm } from '../components/AnketaForm';
import { MyIdCapture } from '../components/MyIdCapture';
import { ClientCard } from '../components/ClientCard';
import { ScoringLoader } from '../components/ScoringLoader';
import { ScoringResults, type OtpBusy } from '../components/ScoringResults';
// Shartnoma komponentlari vaqtincha ishlatilmaydi (RECOMMEND_ONLY, 15.08.2026):
// import { ContractForm } from '../components/ContractForm';
// import { ContractOtp } from '../components/ContractOtp';
// import { ContractDone } from '../components/ContractDone';
import { AppHeader } from '../components/AppHeader';
import { ApiError, api } from '../lib/api';
import { clearFlow, loadFlow, saveFlow } from '../lib/flow-state';
import { useI18n } from '../lib/i18n';

/**
 * Sotuvchi ish oqimi (BT §7.2).
 *
 * anketa → MyID → parallel scoring → natijalar → shartnoma → yakun.
 *
 * Provayder integratsiyalari hozircha mock rejimda (kalitlar kelmagan):
 * oqim to'liq ishlaydi, tashqariga so'rov ketmaydi.
 */

interface Identification {
  identificationId: string;
  mode: 'mock' | 'real';
}

export function FlowPage() {
  const { t, apiMessage, myIdMessage } = useI18n();

  const [step, setStep] = useState<StepId>(1);
  const [identification, setIdentification] = useState<Identification | null>(null);
  const [client, setClient] = useState<IdentifiedClient | null>(null);
  // Mijoz ilgari identifikatsiyadan o'tgan bo'lsa — qachon o'tgani.
  const [reidentified, setReidentified] = useState<string | null>(null);
  const [session, setSession] = useState<ScoringSession | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [failure, setFailure] = useState<VerifyIdentificationResponse | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [choosing, setChoosing] = useState<Provider | null>(null);
  // Kod bilan ish qaysi provayderda ketayotgani: ikkinchi kartochka
  // bloklanmasligi kerak — u o'z natijasini mustaqil kutmoqda.
  const [otpBusy, setOtpBusy] = useState<OtpBusy | null>(null);
  const [otpError, setOtpError] = useState<{ provider: Provider; error: ApiError } | null>(null);
  // Bitta urinish — bitta kalit. Tugma ikki marta bosilsa ham backend
  // ikkinchi scoringni yaratmaydi (BT §6.8).
  const [idempotencyKey, setIdempotencyKey] = useState<string | null>(null);
  /** O'zgarganda anketa formasi qaytadan chiziladi (tozalanadi). */
  const [formKey, setFormKey] = useState(0);
  // Sahifa yangilangan bo'lsa — avval holatni tiklaymiz, keyin ekran chiqadi.
  const [restoring, setRestoring] = useState(() => loadFlow() !== null);

  /**
   * F5 dan keyin ishni davom ettirish.
   *
   * Bosqich saqlangan raqamdan emas, **haqiqatda tiklangan narsadan**
   * hisoblanadi: shartnoma bormi, scoring bormi, mijoz bormi. Shunda
   * server holati bilan ekran hech qachon ziddiyatga tushmaydi.
   */
  useEffect(() => {
    const snapshot = loadFlow();
    if (!snapshot) return;

    let cancelled = false;

    async function restore() {
      // Ariza (shartnoma) tiklanmaydi — bosqich yopiq (RECOMMEND_ONLY)
      const [restoredClient, restoredSession] = await Promise.all([
        snapshot!.clientId
          ? api.get<IdentifiedClient>(`/clients/${snapshot!.clientId}`).catch(() => null)
          : null,
        snapshot!.sessionId
          ? api.get<ScoringSession>(`/scoring/${snapshot!.sessionId}`).catch(() => null)
          : null,
      ]);

      if (cancelled) return;

      if (restoredClient) setClient(restoredClient);
      if (restoredSession) setSession(restoredSession);
      if (snapshot!.identificationId) {
        setIdentification({
          identificationId: snapshot!.identificationId,
          mode: snapshot!.identificationMode ?? 'mock',
        });
      }
      if (snapshot!.idempotencyKey) setIdempotencyKey(snapshot!.idempotencyKey);

      // Shartnoma bosqichlari yopiq (RECOMMEND_ONLY) — natijalarda qolamiz
      if (restoredSession) setStep(4);
      else if (restoredClient) setStep(3);
      else if (snapshot!.identificationId) setStep(2);

      setRestoring(false);
    }

    void restore().finally(() => {
      if (!cancelled) setRestoring(false);
    });

    return () => {
      cancelled = true;
    };
    // Faqat birinchi renderda — keyin holatni oqimning o'zi boshqaradi.
    // eslint-disable-next-line
  }, []);

  function resetFlow() {
    setFormKey((n) => n + 1);
    setStep(1);
    setIdentification(null);
    setClient(null);
    setReidentified(null);
    setSession(null);
    setError(null);
    setFailure(null);
    setIdempotencyKey(null);
    // Yuborish holati ham tozalanadi: aks holda yangi mijozning
    // anketasi bloklangan bo'lib qoladi va sotuvchi hech narsa
    // kirita olmaydi.
    setSubmitting(false);
    clearFlow();
  }

  async function handleAnketa(data: StartIdentificationRequest) {
    setError(null);
    setSubmitting(true);
    try {
      const result = await api.post<StartIdentificationResponse>('/myid/sessions', data);

      // Takroriy mijoz: MyID allaqachon o'tilgan, ikkinchi marta
      // suratga tushirishning hojati yo'q — to'g'ridan-to'g'ri scoringga.
      if (result.client) {
        setClient(result.client);
        setReidentified(result.identifiedAt);
        saveFlow({ clientId: result.client.clientId });
        setStep(3);
        return;
      }

      setIdentification(result);
      saveFlow({
        identificationId: result.identificationId,
        identificationMode: result.mode,
      });
      setStep(2);
    } catch (err) {
      setError(err instanceof ApiError ? err : null);
    } finally {
      setSubmitting(false);
    }
  }

  /**
   * Mijoz telefonda suratga tushayotganini kuzatamiz (QR orqali uzatish).
   *
   * Sotuvchi ekranida hech narsa bosilmaydi: rasm telefondan ketadi,
   * bu yerda faqat natija kutiladi. So'rov 2 soniyada bir marta —
   * bu odam kutadigan tezlik, serverga yuk emas.
   */
  useEffect(() => {
    if (step !== 2 || !identification) return;

    let stopped = false;

    const timer = setInterval(async () => {
      try {
        const state = await api.get<IdentificationStatus>(
          `/myid/sessions/${identification.identificationId}/status`,
        );
        if (stopped) return;

        if (state.status === 'SUCCESS' && state.client) {
          setClient(state.client);
          saveFlow({ clientId: state.client.clientId });
          setStep(3);
        } else if (state.status === 'FAILED') {
          setFailure({
            status: 'FAILED',
            client: null,
            resultCode: state.resultCode,
            message: state.message,
            retryable: state.retryable,
          });
          // Qaytarib bo'lmaydigan xato — kutishning ma'nosi yo'q.
          // Aks holda ekranda xato bilan «telefondagi natija
          // kutilmoqda» birga turadi va sotuvchi nima qilishni bilmaydi.
          if (!state.retryable) {
            stopped = true;
            clearInterval(timer);
          }
        }
      } catch {
        // Tarmoq uzilishi — keyingi urinishda tuzaladi, ekranni buzmaymiz.
      }
    }, 2000);

    return () => {
      stopped = true;
      clearInterval(timer);
    };
  }, [step, identification]);

  /**
   * Scoring tugaguncha natijalarni so'rab turamiz.
   *
   * Server javobni eng sekin provayderni kutmasdan qaytaradi: Uzum
   * yarim soniyada javob beradi, Alif esa limitlarni bir daqiqagacha
   * hisoblashi mumkin. Tayyor bo'lgani darrov ko'rinadi, qolgani
   * «jarayonda» bo'lib turadi va shu yerda yangilanadi.
   */
  useEffect(() => {
    if (step !== 4 || !session) return;
    const waiting = [session.results.alif, session.results.uzum].some(
      (r) =>
        r.status === ProviderResultStatus.PENDING ||
        r.status === ProviderResultStatus.IN_PROGRESS,
    );
    if (!waiting) return;

    let stopped = false;
    // Xavfsizlik chegarasi: server qayta ishga tushsa natija hech
    // qachon kelmasligi mumkin — ekran cheksiz aylanib turmasin.
    const deadline = Date.now() + 3 * 60_000;

    const timer = setInterval(async () => {
      if (Date.now() > deadline) {
        clearInterval(timer);
        return;
      }
      try {
        const fresh = await api.get<ScoringSession>(`/scoring/${session.sessionId}`);
        if (!stopped) setSession(fresh);
      } catch {
        // Tarmoq uzilishi — keyingi urinishda tuzaladi.
      }
    }, 2000);

    return () => {
      stopped = true;
      clearInterval(timer);
    };
  }, [step, session]);

  /**
   * Anketaga qaytish.
   *
   * Identifikatsiya bekor QILINMAYDI: o'sha hujjat bilan qaytib
   * kelinsa, ochiq sessiya qayta ishlatiladi va MyID uchun ikkinchi
   * marta to'lanmaydi. Ekranda esa xato bo'lsa, u yangi urinishga
   * ko'chib o'tmasin.
   */
  function handleBackToAnketa() {
    setFailure(null);
    setError(null);
    setStep(1);
  }

  async function handleCancelIdentification() {
    if (identification) {
      await api
        .post(`/myid/sessions/${identification.identificationId}/cancel`)
        .catch(() => undefined);
    }
    resetFlow();
  }

  /** Bitta tugma — ikkala provayderga parallel so'rov (BT §5, 3-bosqich). */
  async function handleStartScoring() {
    if (!client) return;
    const key = idempotencyKey ?? crypto.randomUUID();
    setIdempotencyKey(key);
    // Kalit so'rovdan OLDIN saqlanadi: scoring ketayotganda sahifa
    // yangilansa ham takroriy so'rov o'sha kalit bilan boradi.
    saveFlow({ idempotencyKey: key });
    setError(null);
    setSubmitting(true);
    try {
      const result = await api.post<ScoringSession>('/scoring', {
        clientId: client.clientId,
        idempotencyKey: key,
      });
      setSession(result);
      saveFlow({ sessionId: result.sessionId });
      setStep(4);
    } catch (err) {
      setError(err instanceof ApiError ? err : null);
    } finally {
      setSubmitting(false);
    }
  }

  /**
   * Scoringni qaytadan yuborish.
   *
   * Provayder texnik sababdan javob bermagan bo'lsa (nosozlik, vaqt
   * tugashi), mijozni boshidan haydashning ma'nosi yo'q: anketa ham,
   * MyID ham o'zgarmagan. Yangi idempotentlik kaliti bilan yangi
   * so'rov ketadi — eskisi takrorlanib qolmasin.
   */
  async function handleRetryScoring() {
    if (!client) return;
    setIdempotencyKey(null);
    saveFlow({ idempotencyKey: undefined, sessionId: undefined });
    setSession(null);
    setStep(3);
    setSubmitting(true);
    try {
      const key = crypto.randomUUID();
      setIdempotencyKey(key);
      saveFlow({ idempotencyKey: key });
      const result = await api.post<ScoringSession>('/scoring', {
        clientId: client.clientId,
        idempotencyKey: key,
      });
      setSession(result);
      saveFlow({ sessionId: result.sessionId });
      setStep(4);
    } catch (err) {
      setError(err instanceof ApiError ? err : null);
      setStep(3);
    } finally {
      setSubmitting(false);
    }
  }

  /**
   * Provayder scoring o'rtasida so'ragan kod (PLAN.md §0.0.1).
   *
   * Faqat shu provayderning kartochkasi bloklanadi — ikkinchisi o'z
   * natijasini mustaqil kutadi.
   */
  async function handleConfirmScoringOtp(provider: Provider, code: string) {
    if (!session) return;
    setOtpError(null);
    setOtpBusy({ provider, action: 'confirm' });
    try {
      const result = await api.post<ScoringSession>(`/scoring/${session.sessionId}/otp`, {
        provider,
        code,
      });
      setSession(result);
    } catch (err) {
      if (err instanceof ApiError) setOtpError({ provider, error: err });
      // Urinishlar soni o'zgardi — holatni serverdan yangilaymiz.
      await refreshSession();
    } finally {
      setOtpBusy(null);
    }
  }

  async function handleResendScoringOtp(provider: Provider) {
    if (!session) return;
    setOtpError(null);
    setOtpBusy({ provider, action: 'resend' });
    try {
      const result = await api.post<ScoringSession>(`/scoring/${session.sessionId}/otp/resend`, {
        provider,
      });
      setSession(result);
    } catch (err) {
      if (err instanceof ApiError) setOtpError({ provider, error: err });
      await refreshSession();
    } finally {
      setOtpBusy(null);
    }
  }

  async function refreshSession() {
    if (!session) return;
    await api
      .get<ScoringSession>(`/scoring/${session.sessionId}`)
      .then(setSession)
      .catch(() => undefined);
  }

  async function handleChoose(provider: Provider) {
    if (!session) return;
    setError(null);
    setChoosing(provider);
    try {
      const result = await api.post<ScoringSession>(`/scoring/${session.sessionId}/choose`, {
        provider,
      });
      setSession(result);
      // Ilgari bu yerda shartnoma bosqichiga o'tilardi (setStep(5)).
      // Hozircha tanlov faqat belgilab qo'yiladi: platforma tavsiya beradi,
      // rasmiylashtirishni provayderning o'z kanalida davom ettiradilar.
    } catch (err) {
      setError(err instanceof ApiError ? err : null);
    } finally {
      setChoosing(null);
    }
  }

  // ── Shartnoma bosqichi vaqtincha yopiq ───────────────────────────────
  // Mijoz qarori (15.08.2026): platforma faqat tavsiya beradi — kim qancha
  // taklif qilyapti. Quyidagi ikkita funksiya API bilan birga joyida
  // turibdi; qaytarish uchun izohdan chiqarish kifoya.
  // /** 5-bosqich: ariza yaratiladi, provayder mijozga OTP yuboradi. */
  // async function handleCreateApplication(data: CreateApplicationRequest) {
  //   setError(null);
  //   setSubmitting(true);
  //   try {
  //     const state = await api.post<ApplicationState>('/applications', data);
  //     setApplication(state);
  //     saveFlow({ applicationId: state.application.id });
  //   } catch (err) {
  //     setError(err instanceof ApiError ? err : null);
  //   } finally {
  //     setSubmitting(false);
  //   }
  // }
  // 
  // /** Mijoz aytgan kod — shundan keyin shartnoma tuziladi. */
  // async function handleConfirmApplication(code: string) {
  //   if (!application) return;
  //   setError(null);
  //   setSubmitting(true);
  //   try {
  //     const state = await api.post<ApplicationState>(
  //       `/applications/${application.application.id}/confirm`,
  //       { code },
  //     );
  //     setApplication(state);
  //     if (state.contract) setStep(6);
  //   } catch (err) {
  //     setError(err instanceof ApiError ? err : null);
  //     // Urinishlar soni o'zgardi — holatni yangilaymiz.
  //     await api
  //       .get<ApplicationState>(`/applications/${application.application.id}`)
  //       .then(setApplication)
  //       .catch(() => undefined);
  //   } finally {
  //     setSubmitting(false);
  //   }
  // }

  return (
    <div className="app-shell">
      <AppHeader />

      <main className="app-main">
        {restoring ? (
          <div className="center-screen" aria-busy="true">
            <div className="spinner" aria-label={t('flow.restoring')} />
          </div>
        ) : (
        <>
        <Stepper current={step} />

        {/* 5-bosqichda xatoni forma o'zi ko'rsatadi — ikki marta chiqmasin. */}
        {error && step < 5 && (
          <div className="alert alert--error" role="alert">
            {apiMessage(error)}
          </div>
        )}

        {/*
          Anketa 2-bosqichda ham chizilgan holda qoladi (ko'rinmasdan):
          sotuvchi orqaga qaytsa, to'ldirgan ma'lumoti joyida turadi.
          Yangi mijozda esa `formKey` o'zgaradi va forma tozalanadi.
        */}
        {step <= 2 && (
          <div hidden={step !== 1}>
            <AnketaForm
              key={formKey}
              onSubmit={handleAnketa}
              error={error}
              submitting={submitting}
            />
          </div>
        )}

        {step === 2 &&
          identification &&
          (failure && !failure.retryable ? (
            // Yakuniy xato: QR ham, «kutilmoqda» ham chiqmaydi —
            // mijoz telefonida boshqa hech narsa bo'lmaydi.
            <div className="card">
              <h2 className="section-title">
                {t(
                  isMyIdSuccess(failure.resultCode)
                    ? 'myid.incompleteTitle'
                    : 'myid.failedTitle',
                )}
              </h2>
              <p className="section-hint">
                {myIdMessage(failure.resultCode, failure.message)}
              </p>
              <button
                className="btn btn--primary btn--wide"
                type="button"
                onClick={handleCancelIdentification}
              >
                {t('myid.startOver')}
              </button>
            </div>
          ) : (
            <>
              {failure && (
                <div className="alert alert--error" role="alert">
                  <strong>{myIdMessage(failure.resultCode, failure.message)}</strong>
                  <div>{t('myid.retry')}</div>
                </div>
              )}
              <MyIdCapture
                identificationId={identification.identificationId}
                mode={identification.mode}
                submitting={submitting}
                onCancel={handleCancelIdentification}
                onBack={handleBackToAnketa}
              />
            </>
          ))}

        {step >= 3 && client && <ClientCard client={client} identifiedAt={reidentified} />}

        {step === 3 &&
          (submitting ? (
            <ScoringLoader />
          ) : (
            <div className="card scoring-start">
              <h2 className="section-title">{t('scoring.title')}</h2>
              <p className="section-hint">{t('scoring.hint')}</p>

              <button
                className="btn btn--primary btn--wide"
                type="button"
                onClick={handleStartScoring}
                disabled={submitting}
              >
                {t('scoring.start')}
              </button>
            </div>
          ))}

        {step >= 4 && session && (
          <ScoringResults
            session={session}
            onChoose={handleChoose}
            choosing={choosing}
            onConfirmOtp={handleConfirmScoringOtp}
            onResendOtp={handleResendScoringOtp}
            otpBusy={otpBusy}
            otpError={otpError}
            onRetry={handleRetryScoring}
            retrying={submitting}
          />
        )}

        {/*
          Shartnoma rasmiylashtirish ekranlari (ContractForm → ContractOtp →
          ContractDone) mijoz qaroriga ko'ra vaqtincha o'chirilgan
          (15.08.2026): platforma faqat kim qancha taklif qilayotganini
          ko'rsatadi. Komponentlar va API o'z joyida — qaytarish uchun shu
          blokni ochish va Stepper'dagi 5–6-bosqichlarni tiklash kifoya.

          {step === 5 && session?.chosenProvider && !application && (
            <ContractForm ... />
          )}
          {step === 5 && application && !application.contract && (
            <ContractOtp ... />
          )}
          {step === 6 && application?.contract && (
            <ContractDone state={application} onNewClient={resetFlow} />
          )}
        */}

        {/* Mijoz kartochkasi chiqqandan boshlab yangi mijozga o'tish mumkin */}
        {step >= 3 && (
          <div className="flow-nav">
            <span />
            <button className="btn btn--ghost" type="button" onClick={resetFlow}>
              {t('flow.newClient')}
            </button>
          </div>
        )}
        </>
        )}
      </main>
    </div>
  );
}
